import type { QueryClient } from 'react-query'
import { PERSIST_DATA, USER_TOKEN, clearImpersonation, removeAuthToken } from './auth'
import { resetPermissionsVersion } from './permissionsVersion'
import storeRef from '@store/storeRef'

// Handled by the root reducer in @store/index — resets EVERY slice (auth,
// profile, ...) back to its initial state, including the redux-persist
// whitelisted ones.
export const USER_LOGOUT = 'USER_LOGOUT'

/**
 * Single teardown path for a session. Clears every place session state lives:
 * localStorage auth token/username, impersonation info, the redux store
 * (via USER_LOGOUT, so persisted auth/profile — incl. permissions — reset too),
 * the persisted redux copy itself, the axios permissions-version tracker, and
 * optionally the react-query cache.
 *
 * queryClient is optional because some callers (e.g. the axios dead-token
 * branch) run outside React and are immediately followed by a full page
 * navigation, which discards the in-memory query cache anyway.
 */
export const clearSession = (queryClient?: QueryClient) => {
  removeAuthToken()
  clearImpersonation()
  resetPermissionsVersion()
  storeRef.current?.dispatch({ type: USER_LOGOUT })
  // redux-persist rewrites storage asynchronously; drop the persisted copy
  // synchronously so a redirect that interrupts the rewrite can't leave the old
  // permissions behind to rehydrate on the next load.
  localStorage.removeItem(PERSIST_DATA)
  // Remembered dashboard filters (common/filterMemory) are deliberately left
  // alone: they are keyed by user id, so they come back on the next sign-in
  // and never surface for a different user on the same browser.
  queryClient?.clear()
}

/**
 * Keeps this tab's session coherent with the shared localStorage token when
 * ANOTHER tab logs in or out ('storage' events never fire in the tab that made
 * the change). Without this, a tab left open keeps its old Redux identity while
 * silently sending the other tab's new token — mixed identity/group context —
 * or keeps rendering an authenticated UI whose token is gone.
 * Call once at boot.
 */
export const initCrossTabSessionSync = () => {
  window.addEventListener('storage', (event) => {
    if (event.key !== USER_TOKEN) return
    const { pathname } = window.location
    // pdfProofReport renders headlessly with a render token — never disturb it.
    if (pathname.includes('pdfProofReport')) return
    if (!event.newValue) {
      // Another tab logged out: tear down local state and show the login page.
      clearSession()
      if (!pathname.startsWith('/login')) window.location.assign('/login')
    } else if (event.newValue !== event.oldValue) {
      // Another tab logged in (or re-logged in): reload so this tab boots
      // cleanly into the new session instead of mixing it with old state.
      window.location.reload()
    }
  })
}
